import { Injectable } from '@angular/core';
import * as dayjs from 'dayjs';
import * as isBetween from 'dayjs/plugin/isBetween';
import * as LocalizedFormat from 'dayjs/plugin/localizedFormat';
import * as Excel from 'exceljs/dist/exceljs.min.js';
import * as FileSaver from 'file-saver';
import * as _ from 'lodash';
import { BehaviorSubject } from 'rxjs';
import { isNullOrUndefined } from 'src/app/services/utils';
import * as XLSX from 'xlsx';
import { BULK_UPLOAD, COMMON_CONSTANTS, EXCEL_JS, SUSPEND_CONSTANTS, VALUE, BULK_TEMPLATE_FILES, MANAGE_LINE_CONST, LINE_IDENTIFIER } from '../common/core/common.constants';
import { ContentService } from './content.service';
import { ManageLineService } from './manage-line.service';
import { HttpCacheService } from './http-cache.service';
import { environment } from 'src/environments/environment';
import { UtilsService } from 'src/app/services/utils.service';

dayjs.extend(LocalizedFormat);
dayjs.extend(isBetween);
const EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
const EXCEL_EXTENSION = '.xlsx';
const CSV_EXTENSION = '.csv';

@Injectable({
  providedIn: 'root',
})
export class ExportService {
  ws_data: any;
  arrayBuffer: any;
  file: File;
  ExceltoJson: any = [];
  popuperror = '';
  templateType: any;
  uploadValidatedResponse: any;
  public uploadJson: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  worksheet: XLSX.WorkSheet;
  bulkFlow: string;
  eSimEnabled:boolean = environment.ENABLE_ESIM;

  constructor(private _cacheSvc: HttpCacheService,
              public manageLineService: ManageLineService, private _utilsSvc: UtilsService) {
    this.uploadJson.asObservable();
    this.manageLineService.ExceltoJson.subscribe((response) => {
      this.uploadJson.next(response);
    });
  }

  private saveAsExcelFile(buffer: any, fileName: string): void {
    const data: Blob = new Blob([buffer], {
      type: EXCEL_TYPE,
    });
    FileSaver.saveAs(data, fileName + EXCEL_EXTENSION);
  }

  public exportAsExcelFile(json: any, excelFileName: string, transDetailsHeader?: any[]) {
    const flowType =  ContentService.GetCache(BULK_UPLOAD.BULK_FLOW_KEY);
    const data_values = [];
    const columnNames = [..._.uniq(json.flatMap(Object.keys))];
    if (!isNullOrUndefined(transDetailsHeader)) {
      const headerKeys = _.flatten(transDetailsHeader.map((obj) => Object.values(obj)));
      this.downloadTransactionHistoryExcel(headerKeys, json, excelFileName);
    } else {
      for (const jsonElement of json) {
        const data = Object.values(jsonElement);
        data_values.push(data);
      }
      switch (flowType) {
        case BULK_UPLOAD.STRATEGIC_PORT_IN:
        case BULK_UPLOAD.TRANSFER_NUMBERS:
          // Used by TRANSFER NUMBER + STRATEGIC PORT IN flows
          this.dataValidationForTransferNumber(columnNames, data_values, excelFileName);
          break;
        case BULK_UPLOAD.CHANGE_PHONE_KEY:
          this.dataValidationForChangeNumber(columnNames, data_values, excelFileName);
          break;
        case BULK_UPLOAD.RESTORE_KEY:
          this.dataValidationForRestoreKey(columnNames, data_values, excelFileName);
          break;
        case BULK_UPLOAD.SUSPEND_KEY:
          this.dataValidationForSuspendKey(columnNames, data_values, excelFileName);
          break;
        case BULK_UPLOAD.SIM_SWAP_KEY:
          this.dataValidationForSimSwapKey(columnNames, data_values, excelFileName);
          break;
        case BULK_UPLOAD.LINE_IDENTIFIER:
          this.dataValidationForLineIdentifier(columnNames, data_values, excelFileName);
          break;
        case BULK_UPLOAD.CANCEL_SUBSCRIBER:
          this.dataValidationForCancelSubscriber(columnNames, data_values, excelFileName);
          break;
        case BULK_UPLOAD.CHANGE_SERVICE:
          this.dataValidationForChangeService(columnNames, data_values, excelFileName);
          break;
        case BULK_UPLOAD.MANAGE_PAIRING:
          this.dataValidationForManagePairing(columnNames, data_values, excelFileName);
          break;
        case BULK_UPLOAD.TRANSACTION_HISTORY:
          this.dataValidationForStrategicPortIn(columnNames, data_values, excelFileName);
          break;
        case BULK_UPLOAD.TPOP_KEY:
          this.dataValidationForTpopKey(columnNames, data_values, excelFileName);
          break;
      }
    }
  }

  async dataValidationForChangeService(columnNames: any[], data_values: any[], fileName: string) {


    const workbook = new Excel.Workbook();
    workbook.views = [{
      x: VALUE.FIVE, y: VALUE.FIVE, width: VALUE.TEN_THOUSAND, height: VALUE.TWENTY_THOUSAND,
      firstSheet: VALUE.ZERO, activeTab: VALUE.ONE, visibility: EXCEL_JS.VISIBLE,
    }];
    const worksheet = workbook.addWorksheet(EXCEL_JS.CHANGE_SERVICE_SHEET);
    worksheet.pageSetup.margins = {
      left: VALUE.POINT_SEVEN, right: VALUE.POINT_SEVEN,
      top: VALUE.POINT_SEVEN_FIVE, bottom: VALUE.POINT_SEVEN_FIVE,
      header: VALUE.POINT_THREE, footer: VALUE.POINT_THREE,
    };
    const temp = [];
    for(const name of columnNames) {
      temp.push({
        header: name,
        key: name,
        width: 40
      });
    }
    worksheet.columns = temp
    worksheet.addRows(data_values);
    const excelColumns = 'abcdefghijklmnopqrstuvwxyz'.toUpperCase().split('');
    worksheet.eachColumnKey((column: any, index: string) => {
      const row1 = worksheet.getRow(1);
      row1.alignment = { wrapText: true };
      row1.font = { bold: true };

      for (let rowCount = VALUE.TWO; rowCount < VALUE.TWO_THOUSAND_FIVE_HUNDRED_ONE; rowCount++) {
        if (column.key === BULK_UPLOAD.BULK_PHONE_NUMBER_TXT) {
          const cell_A = EXCEL_JS.CELL_A + (rowCount);
          worksheet.getCell(cell_A).dataValidation = {
            type: EXCEL_JS.CUSTOM,
            formulae: ['=AND(ISNUMBER(' + cell_A + '),LEN(' + cell_A + ')=10)'],
            operator: EXCEL_JS.OPERATOR_EQUAL,
            showInputMessage: true,
            allowBlank: true,
            promptTitle: EXCEL_JS.PROMPT_TITLE_PHONE_NUMBER,
            prompt: EXCEL_JS.PROMPT_FORMAT_TEN_DIGITS,
            showErrorMessage: true,
            errorStyle: EXCEL_JS.ERROR_STYLE,
            errorTitle: EXCEL_JS.ERROR_TITLE_PHONENUMBER,
            error: EXCEL_JS.ERROR_FORMAT_TEN_DIGITS,
          };
          worksheet.getCell(cell_A).numFmt = EXCEL_JS.NUMBER_FORMAT;
          worksheet.getCell(cell_A).protection = {
            locked: false,
          };
        } else if (column.key === BULK_UPLOAD.ERROR_KEY) {
          const cell = excelColumns[column.number - 1] + (rowCount);
          worksheet.getCell(cell).alignment = { wrapText: true };
          worksheet.getCell(cell).protection = {
            locked: false,
          };
        } else {
          const cell = excelColumns[column.number - 1] + (rowCount);
          worksheet.getCell(cell).dataValidation = {
            type: EXCEL_JS.VALIDATION_TYPE_LIST,
            formulae: [EXCEL_JS.DROPDOWN_VALUES_CHANGE_SERVICE],
            showInputMessage: true,
            allowBlank: true,
            promptTitle: 'Add or Remove Service',
            prompt: EXCEL_JS.PROMPT_FORMAT_REASON_FOR_CHANGE,
            showErrorMessage: true,
            errorStyle: EXCEL_JS.ERROR_STYLE,
            errorTitle: EXCEL_JS.ERROR_TITLE_INVALID_VALUE,
            error: EXCEL_JS.PROMPT_FORMAT_REASON_FOR_CHANGE,
          }
        }
      }
    });

    worksheet.views = [{ state: EXCEL_JS.FROZEN_STATE, xSplit: VALUE.ZERO, ySplit: VALUE.ONE, topLeftCell: EXCEL_JS.CELL_A2, activeCell: EXCEL_JS.CELL_A1 }];
    workbook.xlsx.writeBuffer().then((data: Uint8Array) => {
      const blob = new Blob([data], { type: EXCEL_JS.APPLICATION_FORMAT_TYPE });
      FileSaver.saveAs(blob, fileName + EXCEL_EXTENSION);
    }).catch((err) => {
      console.log(err)
    })
  }

  async dataValidationForCancelSubscriber(columnNames: any[], data_values: any[], fileName: string) {
    const workbook = new Excel.Workbook();
    workbook.views = [{
      x: VALUE.FIVE, y: VALUE.FIVE, width: VALUE.TEN_THOUSAND, height: VALUE.TWENTY_THOUSAND,
      firstSheet: VALUE.ZERO, activeTab: VALUE.ONE, visibility: EXCEL_JS.VISIBLE,
    }];
    const worksheet = workbook.addWorksheet(EXCEL_JS.CHANGE_SERVICE_SHEET);
    worksheet.pageSetup.margins = {
      left: VALUE.POINT_SEVEN, right: VALUE.POINT_SEVEN,
      top: VALUE.POINT_SEVEN_FIVE, bottom: VALUE.POINT_SEVEN_FIVE,
      header: VALUE.POINT_THREE, footer: VALUE.POINT_THREE,
    };

    const tempArrayofColumns = [];
    for (const column of columnNames) {
      tempArrayofColumns.push({
        header: column,
        key: column,
        width: 40
      });
    }
    worksheet.columns = tempArrayofColumns;
    worksheet.addRows(data_values);

    const startDate = this.getCurrentDateForExcel();
    worksheet.eachColumnKey((column: any, index: string) => {
      const row1 = worksheet.getRow(1);
      row1.alignment = { wrapText: true };
      row1.font = { bold: true };

      for (let rowCount = VALUE.TWO; rowCount < VALUE.TWO_THOUSAND_FIVE_HUNDRED_ONE; rowCount++) {
        if (column.key === BULK_UPLOAD.BULK_PHONE_NUMBER_TXT) {
          const cell_A = EXCEL_JS.CELL_A + (rowCount);
          worksheet.getCell(cell_A).dataValidation = {
            type: EXCEL_JS.CUSTOM,
            formulae: ['=AND(ISNUMBER(' + cell_A + '),LEN(' + cell_A + ')=10)'],
            operator: EXCEL_JS.OPERATOR_EQUAL,
            showInputMessage: true,
            allowBlank: true,
            promptTitle: EXCEL_JS.PROMPT_TITLE_PHONE_NUMBER,
            prompt: EXCEL_JS.PROMPT_FORMAT_TEN_DIGITS,
            showErrorMessage: true,
            errorStyle: EXCEL_JS.ERROR_STYLE,
            errorTitle: EXCEL_JS.ERROR_TITLE_PHONENUMBER,
            error: EXCEL_JS.ERROR_FORMAT_TEN_DIGITS,
          };
          worksheet.getCell(cell_A).numFmt = EXCEL_JS.NUMBER_FORMAT;
          worksheet.getCell(cell_A).protection = {
            locked: false,
          };
        } else if (column.key === BULK_UPLOAD.CANCEL_DATE) {
          const cell_B = EXCEL_JS.CELL_B + (rowCount);
          worksheet.getCell(cell_B).dataValidation = {
            type: EXCEL_JS.CUSTOM,
            operator: EXCEL_JS.OPERATOR_BETWEEN,
            formulae: ['=AND(' + cell_B + '>=DATE(' + startDate + '),' + cell_B + '>=DATE(' + startDate + '))'],
            showInputMessage: true,
            allowBlank: true,
            promptTitle: EXCEL_JS.PROMPT_TITLE_CANCEL_SUBSCRIBER_START_DATE,
            prompt: EXCEL_JS.PROMPT_FORMAT_DATE,
            showErrorMessage: true,
            errorStyle: EXCEL_JS.ERROR_STYLE,
            errorTitle: EXCEL_JS.PROMPT_TITLE_CANCEL_SUBSCRIBER_START_DATE,
            error: EXCEL_JS.ERROR_FORMAT_DATE,
          };
          worksheet.getCell(cell_B).numFmt = EXCEL_JS.DATE_FORMAT;
          worksheet.getCell(cell_B).protection = {
            locked: false,
          };
        } else if (column.key === BULK_UPLOAD.CANCEL_REASON) {
          const cell_C = EXCEL_JS.CELL_C + (rowCount);
          worksheet.getCell(cell_C).dataValidation = {
            type: EXCEL_JS.VALIDATION_TYPE_LIST,
            formulae: [EXCEL_JS.DROPDOWN_VALUES_CANCEL_SUBSCRIBER],
            showInputMessage: true,
            allowBlank: true,
            promptTitle: EXCEL_JS.PROMPT_TITLE_REASON_FOR_CHANGE,
            prompt: EXCEL_JS.PROMPT_TITLE_REASON_FOR_CANCELLATION,
            showErrorMessage: true,
            errorStyle: EXCEL_JS.ERROR_STYLE,
            errorTitle: EXCEL_JS.ERROR_TITLE_INVALID_VALUE,
            error: EXCEL_JS.PROMPT_TITLE_REASON_FOR_CANCELLATION,
          };
        } else if (column.key === BULK_UPLOAD.ERROR_KEY) {
          const cell_D = EXCEL_JS.CELL_D + (rowCount);
          worksheet.getCell(cell_D).alignment = { wrapText: true };
          worksheet.getCell(cell_D).protection = {
            locked: false,
          };
        }
      }
    });
    worksheet.views = [{ state: EXCEL_JS.FROZEN_STATE, xSplit: VALUE.ZERO, ySplit: VALUE.ONE, topLeftCell: EXCEL_JS.CELL_A2, activeCell: EXCEL_JS.CELL_A1 }];
    workbook.xlsx.writeBuffer().then((data: Uint8Array) => {
      const blob = new Blob([data], { type: EXCEL_JS.APPLICATION_FORMAT_TYPE });
      FileSaver.saveAs(blob, fileName + EXCEL_EXTENSION);
    }).catch((err) => {
      console.log(err)
    });
  }

  getHeaderforSixthUDF(configModel){
    let headerText = "";
    configModel.forEach(config => {
        if(config?.configName == LINE_IDENTIFIER.CONFIG_UDFID6){
          headerText = BULK_UPLOAD.UPDATED_TXT + COMMON_CONSTANTS.STRING_SPACE + config.configLabel;
        }
      });
      return headerText;
  }

  async dataValidationForLineIdentifier(columnName: any[], data_values: any[], fileName: string) {
    const workbook = new Excel.Workbook();
    let alphabets = [EXCEL_JS.CELL_A, EXCEL_JS.CELL_B, EXCEL_JS.CELL_C, EXCEL_JS.CELL_D, EXCEL_JS.CELL_E, EXCEL_JS.CELL_F, EXCEL_JS.CELL_G, EXCEL_JS.CELL_H, EXCEL_JS.CELL_I, EXCEL_JS.CELL_J, EXCEL_JS.CELL_K, EXCEL_JS.CELL_L, EXCEL_JS.CELL_M, EXCEL_JS.CELL_N];
    // add extra columns to excel if ENABLE_UDF_AHUB_2 is true
    if(environment.ENABLE_UDF_AHUB_2){
      alphabets = [...alphabets, EXCEL_JS.CELL_O,EXCEL_JS.CELL_P,EXCEL_JS.CELL_Q,EXCEL_JS.CELL_R,EXCEL_JS.CELL_S,EXCEL_JS.CELL_T, EXCEL_JS.CELL_U,EXCEL_JS.CELL_V,EXCEL_JS.CELL_W,EXCEL_JS.CELL_X]
    }
    workbook.views = [{
      x: VALUE.FIVE, y: VALUE.FIVE, width: VALUE.TEN_THOUSAND, height: VALUE.TWENTY_THOUSAND,
      firstSheet: VALUE.ZERO, activeTab: VALUE.ONE, visibility: EXCEL_JS.VISIBLE,
    }];
    const worksheet = workbook.addWorksheet(EXCEL_JS.LINE_IDENTIFIER_SHEET);
    worksheet.pageSetup.margins = {
      left: VALUE.POINT_SEVEN, right: VALUE.POINT_SEVEN,
      top: VALUE.POINT_SEVEN_FIVE, bottom: VALUE.POINT_SEVEN_FIVE,
      header: VALUE.POINT_THREE, footer: VALUE.POINT_THREE,
    };

    const tempArrayOfColumns = [];
    for (const column of columnName) {
      tempArrayOfColumns.push({ header: column, key: column, width: VALUE.TWENTY_SEVEN });
    }
    worksheet.columns = tempArrayOfColumns;
    worksheet.addRows(data_values);
    worksheet.eachColumnKey((column: any, index: string) => {
      const row1 = worksheet.getRow(1);
      row1.alignment = { wrapText: true };
      row1.font = { bold: true };
      let configModel = this._utilsSvc.createConfigModel();
      let udf6Check = configModel.some(config => config?.configName == LINE_IDENTIFIER.CONFIG_UDFID6);
      if(environment.ENABLE_DH_UDF && udf6Check){
        let cellNumSixthUDF =0;
        let headerText = this.getHeaderforSixthUDF(configModel);
        row1.values.forEach(element => {
          const cell_SixthUDF = alphabets[cellNumSixthUDF] + COMMON_CONSTANTS.DIGITS.ONE;
          cellNumSixthUDF++;
          if(element.toString().includes(headerText)){
            let note = element.toString().substring(0, worksheet.getCell(cell_SixthUDF)._column._key.toString().lastIndexOf(headerText));
            worksheet.getCell(cell_SixthUDF).value = {
              'richText': [
                {'font': {'italic': true},'text': note},
                {'font': {'bold': true},'text': headerText}
              ]
            };
          }
        });
      }
      
      // Check if the column is a UDF column with pickerList values
      const { isPickerList, pickerListValue } = this.validateUDFAhub2Find(column, configModel);

      for (let rowCount = VALUE.TWO; rowCount < VALUE.TWO_THOUSAND_FIVE_HUNDRED_ONE; rowCount++) {
        if (column.key === BULK_UPLOAD.BAN_TXT) {
          const cell_A = EXCEL_JS.CELL_A + (rowCount);
          worksheet.getCell(cell_A).dataValidation = {
            type: EXCEL_JS.CUSTOM,
            formulae: ['=AND(ISNUMBER(' + cell_A + '),LEN(' + cell_A + ')=9)'],
            operator: EXCEL_JS.OPERATOR_EQUAL,
            allowBlank: true,
            showErrorMessage: true,
            errorStyle: EXCEL_JS.ERROR_STYLE,
            errorTitle: EXCEL_JS.ERROR_TITLE_INVALID_BAN,
            error: EXCEL_JS.ERROR_INVALID_BAN,
          };
          worksheet.getCell(cell_A).protection = {
            locked: false,
          };
        } else if (column.key === BULK_UPLOAD.PHONE_NUMBER_TXT) {
          const cell_B = EXCEL_JS.CELL_B + (rowCount);
          worksheet.getCell(cell_B).dataValidation = {
            type: EXCEL_JS.CUSTOM,
            formulae: ['=AND(ISNUMBER(' + cell_B + '),LEN(' + cell_B + ')=10)'],
            operator: EXCEL_JS.OPERATOR_EQUAL,
            allowBlank: true,
            showErrorMessage: true,
            errorStyle: EXCEL_JS.ERROR_STYLE,
            errorTitle: EXCEL_JS.ERROR_TITLE_INALID_PHONE_NUMBER,
            error: EXCEL_JS.ERROR_INVALID_PHONE_NUMBER,
          };
          worksheet.getCell(cell_B).numFmt = EXCEL_JS.NUMBER_FORMAT_V2;
          worksheet.getCell(cell_B).protection = {
            locked: false,
          };
        } else {
          const cell = alphabets[column._number - COMMON_CONSTANTS.DIGITS.ONE] + (rowCount);
          // Set data validation formula for Line Identifier
          let LINE_IDENTIFIER_FORMULA = '=AND(LEN(' + cell + ')<=80,ISERR(SEARCH("<",' + cell + '))+ISERR(SEARCH(">",' + cell + '))+ISERR(SEARCH("{",' + cell + '))+ISERR(SEARCH("}",' +
            cell + '))+ISERR(SEARCH("[",' + cell + '))+ISERR(SEARCH("]",' + cell + '))+ISERR(SEARCH("~*",' + cell + '))+ ISERR(SEARCH(CHAR(43),' + cell + '))+ ISERR(SEARCH(CHAR(61),' + cell + '))+ ISERR(SEARCH(CHAR(37),' + cell + '))+ ISERR(SEARCH(CHAR(94),' + cell + '))+ISERR(SEARCH(CHAR(39),' + cell + '))+ISERR(SEARCH(CHAR(34),' + cell + '))=13)';
          // Set validation type and operator
          let VALIDATION_TYPE = EXCEL_JS.CUSTOM;
          let LINE_IDENTIFIER_OPERATOR = EXCEL_JS.OPERATOR_EQUAL;

          // Check if the environment variable for UDF AHUB 2 is enabled
          if (environment.ENABLE_UDF_AHUB_2) {     
          
            if (isPickerList) {
              // Prepare list formula for Excel
              const escapedList = pickerListValue.toString();
              // Wrap list string in quotes
              LINE_IDENTIFIER_FORMULA = `"${escapedList}"`;
              // Validation type as LIST
              VALIDATION_TYPE = EXCEL_JS.VALIDATION_TYPE_LIST;
              // Operator is not needed for LIST validation
              LINE_IDENTIFIER_OPERATOR = '';
            } else {
              // If not a picker list, use the existing custom formula
              LINE_IDENTIFIER_FORMULA = '=AND(LEN(' + cell + ')<=80,ISERR(SEARCH("<",' + cell + '))+ISERR(SEARCH(">",' + cell + '))+ISERR(SEARCH("{",' + cell + '))+ISERR(SEARCH("}",' +
                cell + '))+ISERR(SEARCH("[",' + cell + '))+ISERR(SEARCH("]",' + cell + '))+ISERR(SEARCH("~*",' + cell + '))+ ISERR(SEARCH(CHAR(43),' + cell + '))+ ISERR(SEARCH(CHAR(61),' + cell + '))+ ISERR(SEARCH(CHAR(37),' + cell + '))+ ISERR(SEARCH(CHAR(94),' + cell + '))+ISERR(SEARCH(CHAR(39),' + cell + '))+ISERR(SEARCH(CHAR(34),' + cell + '))=13)'  // your existing custom formula
              // Validation type remains CUSTOM
              VALIDATION_TYPE = EXCEL_JS.CUSTOM;
              // Operator remains EQUAL
              LINE_IDENTIFIER_OPERATOR = EXCEL_JS.OPERATOR_EQUAL;
            }
          }
          worksheet.getCell(cell).dataValidation = {
            type: VALIDATION_TYPE, // Use the determined validation type for List or Custom
            formulae: [LINE_IDENTIFIER_FORMULA], // Use the determined formula for List or Custom
            operator: LINE_IDENTIFIER_OPERATOR, //  Use the determined operator for List or Custom 
            allowBlank: true,
            showErrorMessage: true,
            errorStyle: EXCEL_JS.ERROR_STYLE,
            errorTitle: EXCEL_JS.ERROR_TITLE_INVALID_UDF,
            error: EXCEL_JS.ERROR_INVALID_UDF,
          };
          worksheet.getCell(cell).protection = {
            locked: false,
          };
        }
      }
    });
    const noOfColumns = columnName.length;

    worksheet.autoFilter = EXCEL_JS.CELL_A + COMMON_CONSTANTS.DIGITS.ONE + COMMON_CONSTANTS.SYMBOLS.COLON + alphabets[noOfColumns - COMMON_CONSTANTS.DIGITS.ONE] + COMMON_CONSTANTS.DIGITS.ONE;
    worksheet.views = [{ state: EXCEL_JS.FROZEN_STATE, xSplit: VALUE.ZERO, ySplit: VALUE.ONE, topLeftCell: EXCEL_JS.CELL_A2, activeCell: EXCEL_JS.CELL_A1 }];
    this.protectSheet(worksheet);
    // Saving Created Excel Sheet
    workbook.xlsx.writeBuffer().then((data: Uint8Array) => {
      const blob = new Blob([data], { type: EXCEL_JS.APPLICATION_FORMAT_TYPE });
      FileSaver.saveAs(blob, fileName + EXCEL_EXTENSION);
    });
  }

  // Function to verify configModel for UDF AHUB 2 which has pickerList or not
  validateUDFAhub2Find(column: any, configModel: any[]) {
    if(environment.ENABLE_UDF_AHUB_2){
      const columnKey = column?._key?.toLowerCase();
      const UPDATED_TXT = BULK_UPLOAD.UPDATED_TXT.toLowerCase();
      if (!columnKey) return { isPickerList: false, pickerListValue: [] };
    
      const match = configModel.find(config => {
        if (config?.configType !== LINE_IDENTIFIER.PICKLIST) return false;
    
        const label = config.configLabel?.toLowerCase();
        return label && columnKey.includes(label) && columnKey.includes(UPDATED_TXT);
      });
    
      return {
        isPickerList: Boolean(match),
        pickerListValue: match?.pickListValue ?? [],
      };
    }
    else {
      return {
        isPickerList: false,
        pickerListValue: [],
      };
    }
  }  

  async dataValidationForTpopKey(columnName: any[], data_values: any[], fileName: string) {
    const workbook = new Excel.Workbook();
    workbook.views = [{
      x: VALUE.FIVE, y: VALUE.FIVE, width: VALUE.TEN_THOUSAND, height: VALUE.TWENTY_THOUSAND,
      firstSheet: VALUE.ZERO, activeTab: VALUE.ONE, visibility: EXCEL_JS.VISIBLE,
    }];
    const worksheet = workbook.addWorksheet(EXCEL_JS.TPOP_SHEET);
    worksheet.pageSetup.margins = {
      left: VALUE.POINT_SEVEN, right: VALUE.POINT_SEVEN,
      top: VALUE.POINT_SEVEN_FIVE, bottom: VALUE.POINT_SEVEN_FIVE,
      header: VALUE.POINT_THREE, footer: VALUE.POINT_THREE,
    };
   
    worksheet.columns = [
      { header: columnName[VALUE.ZERO], key: columnName[VALUE.ZERO], width: VALUE.SIXTEEN },
      { header: columnName[VALUE.ONE], key: columnName[VALUE.ONE], width: VALUE.SIXTEEN }
    ];
    
    worksheet.addRows(data_values);
    worksheet.eachColumnKey((column: any, index: string) => {
      const row1 = worksheet.getRow(1);
      row1.alignment = { wrapText: true };
      row1.font = { bold: true };

      for (let rowCount = VALUE.TWO; rowCount < VALUE.TWO_THOUSAND_FIVE_HUNDRED_ONE; rowCount++) {
        this.applyExceldataValidation(column,rowCount,worksheet);
      }
    });
    worksheet.views = [{ state: EXCEL_JS.FROZEN_STATE, xSplit: VALUE.ZERO, ySplit: VALUE.ONE, topLeftCell: EXCEL_JS.CELL_A2, activeCell: EXCEL_JS.CELL_A1 }];
    this.protectSheet(worksheet);
    // Saving Created Excel Sheet
    workbook.xlsx.writeBuffer().then((data: Uint8Array) => {
      const blob = new Blob([data], { type: EXCEL_JS.APPLICATION_FORMAT_TYPE });
      FileSaver.saveAs(blob, fileName + EXCEL_EXTENSION);
    });
  }

  applyExceldataValidation(column,rowCount,worksheet){
    if (column.key === BULK_UPLOAD.BULK_ACCOUNT_NUMBER_TXT) {
      const cell_A = EXCEL_JS.CELL_A + (rowCount);
      worksheet.getCell(cell_A).dataValidation = {
        type: EXCEL_JS.CUSTOM,
        formulae: ['=AND(ISNUMBER(' + cell_A + '),LEN(' + cell_A + ')=9)'],
        operator: EXCEL_JS.OPERATOR_EQUAL,
        showInputMessage: true,
        allowBlank: true,
        promptTitle: EXCEL_JS.PROMPT_TITLE_ACCOUNT_NUMBER,
        prompt: EXCEL_JS.PROMPT_FORMAT_NINE_DIGITS,
        showErrorMessage: true,
        errorStyle: EXCEL_JS.ERROR_STYLE,
        errorTitle: EXCEL_JS.ERROR_TITLE_ACCOUNT_NUMBER,
        error: EXCEL_JS.ERROR_INVALID_ACC_NUM,
      };
      worksheet.getCell(cell_A).numFmt = EXCEL_JS.NUMBER_FORMAT_V3;
      worksheet.getCell(cell_A).protection = {
        locked: false,
      };
    }

    if (column.key === BULK_UPLOAD.BULK_PHONE_NUMBER_TXT) {
      const cell_B = EXCEL_JS.CELL_B + (rowCount);
      worksheet.getCell(cell_B).dataValidation = {
        type: EXCEL_JS.CUSTOM,
        formulae: ['=AND(ISNUMBER(' + cell_B + '),LEN(' + cell_B + ')=10)'],
        operator: EXCEL_JS.OPERATOR_EQUAL,
        showInputMessage: true,
        allowBlank: true,
        promptTitle: EXCEL_JS.PROMPT_TITLE_PHONE_NUMBER,
        prompt: EXCEL_JS.PROMPT_FORMAT_TEN_DIGITS,
        showErrorMessage: true,
        errorStyle: EXCEL_JS.ERROR_STYLE,
        errorTitle: EXCEL_JS.ERROR_TITLE_PHONE_NUMBER,
        error: EXCEL_JS.ERROR_INVALID_PHONE_NUMBER,
      };
      worksheet.getCell(cell_B).numFmt = EXCEL_JS.NUMBER_FORMAT_V2;
      worksheet.getCell(cell_B).protection = {
        locked: false,
      };
    }
  }

  async dataValidationForSimSwapKey(columnName: any[], data_values: any[], fileName: string) {
    const workbook = new Excel.Workbook();
    workbook.views = [{
      x: VALUE.FIVE, y: VALUE.FIVE, width: VALUE.TEN_THOUSAND, height: VALUE.TWENTY_THOUSAND,
      firstSheet: VALUE.ZERO, activeTab: VALUE.ONE, visibility: EXCEL_JS.VISIBLE,
    }];
    const worksheet = workbook.addWorksheet(EXCEL_JS.SIMSWAP_SHEET);
    worksheet.pageSetup.margins = {
      left: VALUE.POINT_SEVEN, right: VALUE.POINT_SEVEN,
      top: VALUE.POINT_SEVEN_FIVE, bottom: VALUE.POINT_SEVEN_FIVE,
      header: VALUE.POINT_THREE, footer: VALUE.POINT_THREE,
    };

    if(this.eSimEnabled){
      worksheet.columns = [
        { header: columnName[VALUE.ZERO], key: columnName[VALUE.ZERO], width: VALUE.SIXTEEN },
        { header: columnName[VALUE.ONE], key: columnName[VALUE.ONE], width: VALUE.TWENTY_THREE },
        { header: columnName[VALUE.TWO], key: columnName[VALUE.TWO], width: VALUE.FIFTY },
        { header: columnName[VALUE.THREE], key: columnName[VALUE.THREE], width: VALUE.THIRTY_ONE },
        { header: columnName[VALUE.FOUR], key: columnName[VALUE.FOUR], width: VALUE.TWENTY_SEVEN },
        { header: columnName[VALUE.FIVE], key: columnName[VALUE.FIVE], width: VALUE.NINETY },
      ];
    }else{
      worksheet.columns = [
        { header: columnName[VALUE.ZERO], key: columnName[VALUE.ZERO], width: VALUE.SIXTEEN },
        { header: columnName[VALUE.ONE], key: columnName[VALUE.ONE], width: VALUE.TWENTY_THREE },
        { header: columnName[VALUE.TWO], key: columnName[VALUE.TWO], width: VALUE.TWENTY_THREE },
        { header: columnName[VALUE.THREE], key: columnName[VALUE.THREE], width: VALUE.TWENTY_SEVEN },
        { header: columnName[VALUE.FOUR], key: columnName[VALUE.FOUR], width: VALUE.FIFTY_FIVE },
      ];
    }

    worksheet.addRows(data_values);
    worksheet.eachColumnKey((column: any, index: string) => {
      const row1 = worksheet.getRow(1);
      row1.alignment = { wrapText: true };
      row1.font = { bold: true };

      for (let rowCount = VALUE.TWO; rowCount < VALUE.TWO_THOUSAND_FIVE_HUNDRED_ONE; rowCount++) {
        this.assignExceldataValidation(column,rowCount,worksheet);
      }
    });
    worksheet.views = [{ state: EXCEL_JS.FROZEN_STATE, xSplit: VALUE.ZERO, ySplit: VALUE.ONE, topLeftCell: EXCEL_JS.CELL_A2, activeCell: EXCEL_JS.CELL_A1 }];
    this.protectSheet(worksheet);
    // Saving Created Excel Sheet
    workbook.xlsx.writeBuffer().then((data: Uint8Array) => {
      const blob = new Blob([data], { type: EXCEL_JS.APPLICATION_FORMAT_TYPE });
      FileSaver.saveAs(blob, fileName + EXCEL_EXTENSION);
    });
  }
  assignExceldataValidation(column,rowCount,worksheet){
    if (column.key === BULK_UPLOAD.BULK_PHONE_NUMBER_TXT) {
      const cell_A = EXCEL_JS.CELL_A + (rowCount);
      worksheet.getCell(cell_A).dataValidation = {
        type: EXCEL_JS.CUSTOM,
        formulae: ['=AND(ISNUMBER(' + cell_A + '),LEN(' + cell_A + ')=10)'],
        operator: EXCEL_JS.OPERATOR_EQUAL,
        showInputMessage: true,
        allowBlank: true,
        promptTitle: EXCEL_JS.PROMPT_TITLE_PHONE_NUMBER,
        prompt: EXCEL_JS.PROMPT_FORMAT_TEN_DIGITS,
        showErrorMessage: true,
        errorStyle: EXCEL_JS.ERROR_STYLE,
        errorTitle: EXCEL_JS.ERROR_TITLE_PHONENUMBER,
        error: EXCEL_JS.ERROR_FORMAT_TEN_DIGITS,
      };
      worksheet.getCell(cell_A).numFmt = EXCEL_JS.NUMBER_FORMAT_V2;
      worksheet.getCell(cell_A).protection = {
        locked: false,
      };
    }
    if (column.key === BULK_UPLOAD.BULK_SIMSWAP_OLDSIM_Txt) {
      const cell_B = EXCEL_JS.CELL_B + (rowCount);
      worksheet.getCell(cell_B).dataValidation = {
        type: EXCEL_JS.CUSTOM,
        // tslint:disable-next-line: max-line-length
        formulae: ['=AND(ISNUMBER(LEFT(' + cell_B + ',19)+0),EXACT(LEFT(' + cell_B + ',4),"8901"),OR(LEN(' + cell_B + ')=20,LEN(' + cell_B + ')=19),ISNUMBER(SUMPRODUCT(SEARCH(MID(' + cell_B + ',20,1),"fF"))))'],
        operator: EXCEL_JS.OPERATOR_EQUAL,
        showInputMessage: true,
        allowBlank: true,
        promptTitle: EXCEL_JS.PROMPT_TITLE_SIMSWAP_KEY,
        prompt: EXCEL_JS.PROMPT_SIMSWAP_KEY,
        showErrorMessage: true,
        errorStyle: EXCEL_JS.ERROR_STYLE,
        errorTitle: EXCEL_JS.ERROR_TITLE_SIMSWAP_KEY,
        error: EXCEL_JS.ERROR_SIMSWAP_KEY,
      };
      worksheet.getCell(cell_B).numFmt = EXCEL_JS.TEXT_FORMAT;
      worksheet.getCell(cell_B).protection = {
        locked: false,
      };
    }
    if (this.eSimEnabled && column.key === BULK_UPLOAD.BULK_SIMSWAP_EID_Txt) {
      const cell_C = EXCEL_JS.CELL_C + (rowCount);
      worksheet.getCell(cell_C).dataValidation = {
        type: EXCEL_JS.CUSTOM,
        // tslint:disable-next-line: max-line-length
        formulae: ['=AND(EXACT(LEFT(' + cell_C + ',2),"89"),LEN(' + cell_C + ')=32,OR(ISNUMBER(' + cell_C + '),ISNUMBER(SUMPRODUCT(SEARCH(MID(' + cell_C + ',ROW(INDIRECT("1:"&LEN(' + cell_C + '))),1),"0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ")))))'],
        operator: EXCEL_JS.OPERATOR_EQUAL,
        showInputMessage: true,
        allowBlank: true,
        promptTitle: EXCEL_JS.PROMPT_TITLE_SIMSWAP_EID_KEY,
        prompt: EXCEL_JS.PROMPT_ESIMSWAP_KEY,
        showErrorMessage: true,
        errorStyle: EXCEL_JS.ERROR_STYLE,
        errorTitle: EXCEL_JS.ERROR_TITLE_SIMSWAP_KEY,
        error: EXCEL_JS.ERROR_ESIMSWAP_KEY,
      };
      worksheet.getCell(cell_C).numFmt = EXCEL_JS.TEXT_FORMAT;
      worksheet.getCell(cell_C).protection = {
        locked: false,
      };
    }
    if (column.key === BULK_UPLOAD.BULK_SIMSWAP_NEWSIM_Txt || (this.eSimEnabled && column.key === BULK_UPLOAD.BULK_ESIMSWAP_NEWSIM_Txt)) {
      let columnOrder1:any;
      if(this.eSimEnabled){
        columnOrder1 = EXCEL_JS.CELL_D + (rowCount);
      }else{
        columnOrder1 = EXCEL_JS.CELL_C + (rowCount);
      }
      worksheet.getCell(columnOrder1).dataValidation = {
        type: EXCEL_JS.CUSTOM,
        // tslint:disable-next-line: max-line-length
        formulae: ['=AND(ISNUMBER(LEFT(' + columnOrder1 + ',19)+0),EXACT(LEFT(' + columnOrder1 + ',4),"8901"),OR(LEN(' + columnOrder1 + ')=20,LEN(' + columnOrder1 + ')=19),ISNUMBER(SUMPRODUCT(SEARCH(MID(' + columnOrder1 + ',20,1),"fF"))))'],
        operator: EXCEL_JS.OPERATOR_EQUAL,
        showInputMessage: true,
        allowBlank: true,
        promptTitle: EXCEL_JS.PROMPT_TITLE_SIMSWAP_NEW_KEY,
        prompt: EXCEL_JS.PROMPT_SIMSWAP_KEY,
        showErrorMessage: true,
        errorStyle: EXCEL_JS.ERROR_STYLE,
        errorTitle: EXCEL_JS.ERROR_TITLE_SIMSWAP_KEY,
        error: EXCEL_JS.ERROR_SIMSWAP_KEY,
      };
      worksheet.getCell(columnOrder1).numFmt = EXCEL_JS.TEXT_FORMAT;
      worksheet.getCell(columnOrder1).protection = {
        locked: false,
      };
    }
    if (column.key === BULK_UPLOAD.BULK_SIMSWAP_REASON_FOR_CHANGE_Txt) {
      let columnOrder2:any;
      if(this.eSimEnabled){
        columnOrder2 = EXCEL_JS.CELL_E + (rowCount);
      }else{
        columnOrder2 = EXCEL_JS.CELL_D + (rowCount);
      }

      worksheet.getCell(columnOrder2).dataValidation = {
        type: EXCEL_JS.VALIDATION_TYPE_LIST,
        formulae: [EXCEL_JS.DROPDOWN_VALUES_SIM_SWAP_DROPDOWN],
        showInputMessage: true,
        allowBlank: true,
        promptTitle: EXCEL_JS.ERROR_TITLE_INVALID_VALUE,
        prompt: EXCEL_JS.PROMPT_FORMAT_REASON_FOR_CHANGE,
        showErrorMessage: true,
        errorStyle: EXCEL_JS.ERROR_STYLE,
        errorTitle: EXCEL_JS.PROMPT_FORMAT_REASON_FOR_CHANGE,
        error: EXCEL_JS.ERROR_FIELD_NOT_EMPTY,
      };
      worksheet.getCell(columnOrder2).protection = {
        locked: false,
      };
    }
    this.notesColumnSimswap(column,rowCount,worksheet);
  }
  notesColumnSimswap(column,rowCount,worksheet){
    if (column.key === BULK_UPLOAD.SIM_SWAP_NOTE || (this.eSimEnabled && column.key === BULK_UPLOAD.ESIM_SWAP_NOTE)) {
      let columnOrder3:any;
      if(this.eSimEnabled){
        columnOrder3 = EXCEL_JS.CELL_F + (rowCount);
      }else{
        columnOrder3 = EXCEL_JS.CELL_E + (rowCount);
      }
      worksheet.getCell(columnOrder3).dataValidation = {
      };
      worksheet.getCell(columnOrder3).numFmt = EXCEL_JS.NUMBER_FORMAT;
      worksheet.getCell(columnOrder3).protection = {
        locked: false,
      };
    }
  }
  async dataValidationForSuspendKey(columnName: any[], data_values: any[], fileName: string) {
    const isOkta =  this._cacheSvc.getFromSessionStorage(COMMON_CONSTANTS.AUTH_TYPE) !== COMMON_CONSTANTS.IAM_KEY ?  true : false;
    const startDate = this.getCurrentDateForExcel();
    const workbook = new Excel.Workbook();
    workbook.views = [{
      x: VALUE.FIVE, y: VALUE.FIVE, width: VALUE.TEN_THOUSAND, height: VALUE.TWENTY_THOUSAND,
      firstSheet: VALUE.ZERO, activeTab: VALUE.ONE, visibility: EXCEL_JS.VISIBLE,
    }];
    const worksheet = workbook.addWorksheet(EXCEL_JS.SUSPEND_SHEET);
    this.implementNamedRanges(workbook);
    worksheet.pageSetup.margins = {
      left: VALUE.POINT_SEVEN, right: VALUE.POINT_SEVEN,
      top: VALUE.POINT_SEVEN_FIVE, bottom: VALUE.POINT_SEVEN_FIVE,
      header: VALUE.POINT_THREE, footer: VALUE.POINT_THREE,
    };
    const suspendApproverTypeHeader = columnName[VALUE.TWO] === BULK_UPLOAD.BULK_SUSPEND_APPROVER_TXT ? columnName[VALUE.TWO] : BULK_UPLOAD.BULK_SUSPEND_APPROVER_TXT;
    const suncNote = EXCEL_JS.SUSPEND_KEY_NOTE;
    worksheet.columns = [
      { header: columnName[VALUE.ZERO], key: columnName[VALUE.ZERO], width: VALUE.SIXTEEN },
      { header: columnName[VALUE.ONE], key: columnName[VALUE.ONE], width: VALUE.THIRTY_ONE },
      { header: suspendApproverTypeHeader, key: suspendApproverTypeHeader, width: VALUE.SIXTY_FOUR },
      { header: columnName[VALUE.THREE], key: columnName[VALUE.THREE], width: VALUE.TWENTY_ONE },
      { header: columnName[VALUE.FOUR], key: columnName[VALUE.FOUR], width: VALUE.TWENTY_ONE },
      { header: suncNote, key: suncNote, width: VALUE.HUNDRED_TEN },
    ];
    worksheet.addRows(data_values);

    worksheet.eachColumnKey((column: any, index: string) => {
      const row1 = worksheet.getRow(1);
      row1.alignment = { wrapText: true };
      row1.font = { bold: true };

      const cellF2 = worksheet.getCell('F2');
      cellF2.value = EXCEL_JS.SUSPEND_KEY_NOTE_2;
      cellF2.alignment = { wrapText: true };
      cellF2.font = { bold: true };

      for (let rowCount = VALUE.TWO; rowCount < VALUE.TWO_THOUSAND_FIVE_HUNDRED_ONE; rowCount++) {

        if (column.key === BULK_UPLOAD.BULK_PHONE_NUMBER_TXT) {
          const cell_A = EXCEL_JS.CELL_A + (rowCount);
          worksheet.getCell(cell_A).dataValidation = {
            type: EXCEL_JS.CUSTOM,
            formulae: ['=AND(ISNUMBER(' + cell_A + '),LEN(' + cell_A + ')=10)'],
            operator: EXCEL_JS.OPERATOR_EQUAL,
            showInputMessage: true,
            allowBlank: true,
            promptTitle: EXCEL_JS.PROMPT_TITLE_PHONE_NUMBER,
            prompt: EXCEL_JS.PROMPT_FORMAT_TEN_DIGITS,
            showErrorMessage: true,
            errorStyle: EXCEL_JS.ERROR_STYLE,
            errorTitle: EXCEL_JS.ERROR_TITLE_PHONENUMBER,
            error: EXCEL_JS.ERROR_FORMAT_TEN_DIGITS,
          };
          worksheet.getCell(cell_A).numFmt = EXCEL_JS.NUMBER_FORMAT;
          worksheet.getCell(cell_A).protection = {
            locked: false,
          };
        }

        if (column.key === BULK_UPLOAD.BULK_SUSPEND_REASON_TXT) {
          const cell_B = EXCEL_JS.CELL_B + (rowCount);
          let reasonValues =  isOkta ? EXCEL_JS.DROPDOWN_VALUES_SUSPEND_REASON : EXCEL_JS.DROPDOWN_VALUES_SUSPEND_REASON_IAM;
          const selectedBanDetails = JSON.parse(this._cacheSvc.getFromSessionStorage(MANAGE_LINE_CONST.SELECTED_BAN_DETAIL));
          const accountType = selectedBanDetails?.accountType || '';
          if (isOkta && accountType === SUSPEND_CONSTANTS.ACCOUNT_TYPE_G) {
            reasonValues = EXCEL_JS.DROPDOWN_VALUES_SUSPEND_REASON_ACC_TYPE_G;
          }
          worksheet.getCell(cell_B).dataValidation = {
            type: EXCEL_JS.VALIDATION_TYPE_LIST,
            formulae: [reasonValues],
            showInputMessage: true,
            allowBlank: true,
            promptTitle: EXCEL_JS.PROMPT_TITLE_SUSPEND_REASON_DROPDOWN,
            prompt: EXCEL_JS.PROMPT_FORMAT_REASON_FOR_CHANGE,
            showErrorMessage: true,
            errorStyle: EXCEL_JS.ERROR_STYLE,
            errorTitle: EXCEL_JS.ERROR_TITLE_INVALID_VALUE,
            error: EXCEL_JS.PROMPT_FORMAT_REASON_FOR_CHANGE,
          };
          worksheet.getCell(cell_B).protection = {
            locked: false,
          };
        }

        if (column.key === BULK_UPLOAD.BULK_SUSPEND_APPROVER_TXT) {
          const cell_C = EXCEL_JS.CELL_C + (rowCount);
          worksheet.getCell(cell_C).dataValidation = {
            type: EXCEL_JS.VALIDATION_TYPE_LIST,
            formulae: ['=INDIRECT($' + EXCEL_JS.CELL_B + '$' + rowCount + ')'],
            showInputMessage: true,
            allowBlank: true,
            promptTitle: EXCEL_JS.PROMPT_TITLE_SUSPEND_REASON_DROPDOWN,
            prompt: EXCEL_JS.PROMPT_FORMAT_REASON_FOR_CHANGE,
            showErrorMessage: true,
            errorStyle: EXCEL_JS.ERROR_STYLE,
            errorTitle: EXCEL_JS.ERROR_TITLE_INVALID_VALUE,
            error: EXCEL_JS.PROMPT_FORMAT_REASON_FOR_CHANGE,
          };
          worksheet.getCell(cell_C).protection = {
            locked: false,
          };
        }

        if (column.key === BULK_UPLOAD.BULK_SUSPEND_STARTDATE_TXT) {
          const cell_D = EXCEL_JS.CELL_D + (rowCount);
          worksheet.getCell(cell_D).dataValidation = {
            type: EXCEL_JS.CUSTOM,
            operator: EXCEL_JS.OPERATOR_BETWEEN,
            formulae: ['=AND(' + cell_D + '>=DATE(' + startDate + '),' + cell_D + '>=DATE(' + startDate + '))'],
            showInputMessage: true,
            allowBlank: true,
            promptTitle: EXCEL_JS.PROMPT_TITLE_SUSPEND_START_DATE,
            prompt: EXCEL_JS.PROMPT_FORMAT_DATE,
            showErrorMessage: true,
            errorStyle: EXCEL_JS.ERROR_STYLE,
            errorTitle: EXCEL_JS.PROMPT_TITLE_SUSPEND_START_DATE,
            error: EXCEL_JS.ERROR_FORMAT_DATE,
          };
          worksheet.getCell(cell_D).numFmt = EXCEL_JS.DATE_FORMAT;
          worksheet.getCell(cell_D).protection = {
            locked: false,
          };
        }

        if (column.key === BULK_UPLOAD.BULK_SUSPEND_ENDDATE_TXT) {
          const cell_E = EXCEL_JS.CELL_E + (rowCount);
          worksheet.getCell(cell_E).dataValidation = {
            type: EXCEL_JS.CUSTOM,
            operator: EXCEL_JS.OPERATOR_BETWEEN,
            formulae: ['=AND(' + cell_E + '>=DATE(' + startDate + '),' + cell_E + '>=DATE(' + startDate + '))'],
            showInputMessage: true,
            allowBlank: true,
            promptTitle: EXCEL_JS.PROMPT_TITLE_SUSPEND_END_DATE,
            prompt: EXCEL_JS.PROMPT_FORMAT_DATE,
            showErrorMessage: true,
            errorStyle: EXCEL_JS.ERROR_STYLE,
            errorTitle: EXCEL_JS.PROMPT_TITLE_SUSPEND_END_DATE,
            error: EXCEL_JS.ERROR_FORMAT_DATE,
          };
          worksheet.getCell(cell_E).numFmt = EXCEL_JS.DATE_FORMAT;
          worksheet.getCell(cell_E).protection = {
            locked: false,
          };
        }
      }
    });
    worksheet.views = [{ state: EXCEL_JS.FROZEN_STATE, xSplit: VALUE.ZERO, ySplit: VALUE.ONE, topLeftCell: EXCEL_JS.CELL_A2, activeCell: EXCEL_JS.CELL_A1 }];
    this.protectSheet(worksheet);
    // Saving Created Excel Sheet
    workbook.xlsx.writeBuffer().then((data: Uint8Array) => {
      const blob = new Blob([data], { type: EXCEL_JS.APPLICATION_FORMAT_TYPE });
      FileSaver.saveAs(blob, fileName + EXCEL_EXTENSION);
    });
  }

  // Used by TRANSFER NUMBER + STRATEGIC PORT IN flows
  async dataValidationForTransferNumber(columnName: any[], data_values: any[], fileName: string) {
    const startDate = this.getCurrentDatePST();
    const endDate = this.getMaxPortInDatePST();
    const workbook = new Excel.Workbook();
    workbook.views = [{
      x: VALUE.FIVE, y: VALUE.FIVE, width: VALUE.TEN_THOUSAND, height: VALUE.TWENTY_THOUSAND,
      firstSheet: VALUE.ZERO, activeTab: VALUE.ONE, visibility: EXCEL_JS.VISIBLE,
    }];
    const worksheet = workbook.addWorksheet(EXCEL_JS.TRANSFER_NUMBER_SHEET);

    worksheet.pageSetup.margins = {
      left: VALUE.POINT_SEVEN, right: VALUE.POINT_SEVEN,
      top: VALUE.POINT_SEVEN_FIVE, bottom: VALUE.POINT_SEVEN_FIVE,
      header: VALUE.POINT_THREE, footer: VALUE.POINT_THREE,
    };

    worksheet.columns = [
      { header: columnName[VALUE.ZERO], key: columnName[VALUE.ZERO], width: VALUE.NINETEEN },
      { header: columnName[VALUE.ONE], key: columnName[VALUE.ONE], width: VALUE.NINETEEN },
      { header: columnName[VALUE.TWO], key: columnName[VALUE.TWO], width: VALUE.THIRTY_ONE },
      { header: columnName[VALUE.THREE], key: columnName[VALUE.THREE], width: VALUE.THIRTY_TWO },
      { header: columnName[VALUE.FOUR], key: columnName[VALUE.FOUR], width: VALUE.TWENTY_FOUR },
      { header: columnName[VALUE.FIVE], key: columnName[VALUE.FIVE], width: VALUE.THIRTY_ONE },
      { header: columnName[VALUE.SIX], key: columnName[VALUE.SIX], width: VALUE.FIFTEEN },
    ];

    if (worksheet.columns[VALUE.SIX].header === BULK_UPLOAD.ERROR_KEY) {
      const idCol = worksheet.getColumn(BULK_UPLOAD.ERROR_KEY);
      idCol.width = VALUE.FIFTY;
    }

    worksheet.addRows(data_values);

    // Data Validation and Formatings for Each Column
    worksheet.eachColumnKey((column: any, index: string) => {
      const row1 = worksheet.getRow(1);
      row1.alignment = { wrapText: true };
      row1.font = { bold: true };

      for (let rowCount = VALUE.TWO; rowCount < VALUE.TWO_THOUSAND_FIVE_HUNDRED_ONE; rowCount++) {

        if (column.key === BULK_UPLOAD.MSISDN_NUMBER_TXT) {
          const cell_A = EXCEL_JS.CELL_A + (rowCount);
          worksheet.getCell(cell_A).dataValidation = {
            type: EXCEL_JS.CUSTOM,
            formulae: ['=AND(ISNUMBER(' + cell_A + '),LEN(' + cell_A + ')=10)'],
            operator: EXCEL_JS.OPERATOR_EQUAL,
            showInputMessage: true,
            allowBlank: true,
            promptTitle: EXCEL_JS.PROMPT_TITLE_MSISDN,
            prompt: EXCEL_JS.PROMPT_FORMAT_TEN_DIGITS,
            showErrorMessage: true,
            errorStyle: EXCEL_JS.ERROR_STYLE,
            errorTitle: EXCEL_JS.ERROR_TITLE_MSISDN,
            error: EXCEL_JS.ERROR_FORMAT_TEN_DIGITS,
          };
          worksheet.getCell(cell_A).numFmt = EXCEL_JS.NUMBER_FORMAT;
          worksheet.getCell(cell_A).protection = {
            locked: false,
          };
        }

        if (column.key === BULK_UPLOAD.NUMBER_TO_TRANSFER_TXT) {
          const cell_B = EXCEL_JS.CELL_B + (rowCount);
          worksheet.getCell(cell_B).dataValidation = {
            type: EXCEL_JS.CUSTOM,
            formulae: ['=AND(ISNUMBER(' + cell_B + '),LEN(' + cell_B + ')=10)'],
            operator: EXCEL_JS.OPERATOR_EQUAL,
            showInputMessage: true,
            allowBlank: true,
            promptTitle: EXCEL_JS.PROMPT_TITLE_TRANSFER_NUMBER,
            prompt: EXCEL_JS.PROMPT_FORMAT_TEN_DIGITS,
            showErrorMessage: true,
            errorStyle: EXCEL_JS.ERROR_STYLE,
            errorTitle: EXCEL_JS.ERROR_TITLE_INVALID_MSISDN,
            error: EXCEL_JS.ERROR_FORMAT_FOR_MSISDN,
          };
          worksheet.getCell(cell_B).numFmt = EXCEL_JS.NUMBER_FORMAT;
          worksheet.getCell(cell_B).protection = {
            locked: false,
          };
        }

        if (column.key === BULK_UPLOAD.CARRIER_ACCOUNT_NUMBER) {
          const cell_C = EXCEL_JS.CELL_C + (rowCount);
          worksheet.getCell(cell_C).dataValidation = {
            type: EXCEL_JS.VALIDATION_TYPE_TEXT_LENGTH,
            operator: EXCEL_JS.OPERATOR_BETWEEN,
            formulae: [VALUE.ONE, VALUE.THIRTY],
            showInputMessage: true,
            allowBlank: true,
            promptTitle: EXCEL_JS.PROMPT_TITLE_CARRIER_NUMBER,
            prompt: EXCEL_JS.PROMPT_FORMAT_THIRTY_CHAR,
            showErrorMessage: true,
            errorStyle: EXCEL_JS.ERROR_STYLE,
            errorTitle: EXCEL_JS.ERROR_TITLE_CARRIER_NUMBER,
            error: EXCEL_JS.ERROR_FORMAT_THIRTY_CHAR,
          };
          worksheet.getCell(cell_C).numFmt = EXCEL_JS.TEXT_FORMAT;
          worksheet.getCell(cell_C).protection = {
            locked: false,
          };
        }

        if (column.key === BULK_UPLOAD.ACCOUNT_PIN_OR_PASSWORD) {
          const cell_D = EXCEL_JS.CELL_D + (rowCount);
          worksheet.getCell(cell_D).dataValidation = {
            type: EXCEL_JS.VALIDATION_TYPE_TEXT_LENGTH,
            operator: EXCEL_JS.OPERATOR_BETWEEN,
            formulae: [VALUE.ONE, VALUE.THIRTY],
            showInputMessage: true,
            allowBlank: true,
            promptTitle: EXCEL_JS.PROMPT_TITLE_PIN_PASSWORD,
            prompt: EXCEL_JS.PROMPT_FORMAT_THIRTY_CHAR,
            showErrorMessage: true,
            errorStyle: EXCEL_JS.ERROR_STYLE,
            errorTitle: EXCEL_JS.ERROR_TITLE_PIN_PASSWORD,
            error: EXCEL_JS.ERROR_FORMAT_THIRTY_CHAR,
          };
          worksheet.getCell(cell_D).numFmt = EXCEL_JS.TEXT_FORMAT;
          worksheet.getCell(cell_D).protection = {
            locked: false,
          };
        }

        if (column.key === BULK_UPLOAD.PORT_DATE_TXT) {
          const cell_E = EXCEL_JS.CELL_E + (rowCount);
          worksheet.getCell(cell_E).dataValidation = {
            type: EXCEL_JS.CUSTOM,
            operator: EXCEL_JS.OPERATOR_BETWEEN,
            formulae: [['=AND(' + cell_E + '>=DATE(' + startDate + '),' + cell_E + '<=DATE(' + endDate + '))']],
            showInputMessage: true,
            allowBlank: true,
            promptTitle: EXCEL_JS.PROMPT_TITLE_PORT_DATE,
            prompt: EXCEL_JS.PROMPT_FORMAT_DATE,
            showErrorMessage: true,
            errorStyle: EXCEL_JS.ERROR_STYLE,
            errorTitle: EXCEL_JS.ERROR_TITLE_PORT_DATE,
            error: EXCEL_JS.ERROR_FORMAT_DATE,
          };
          worksheet.getCell(cell_E).numFmt = EXCEL_JS.DATE_FORMAT;
          worksheet.getCell(cell_E).protection = {
            locked: false,
          };
        }

        if (column.key === BULK_UPLOAD.PORT_START_TIME_TXT) {
          const cell_F = EXCEL_JS.CELL_F + (rowCount);
          worksheet.getCell(cell_F).dataValidation = {
            type: EXCEL_JS.VALIDATION_TYPE_TIME,
            allowBlank: true,
            operator: EXCEL_JS.OPERATOR_BETWEEN,
            formulae: [0, 0.999988425925926],//these values transalate in excel to 12:00:00 AM and 11:59:59 PM
            showInputMessage: true,
            promptTitle: EXCEL_JS.PROMPT_TITLE_PORT_TIME,
            prompt: EXCEL_JS.PROMPT_FORMAT_TIME,
            showErrorMessage: true,
            errorStyle: EXCEL_JS.ERROR_STYLE,
            errorTitle: EXCEL_JS.ERROR_TITLE_PORT_TIME,
            error: EXCEL_JS.ERROR_FORMAT_TIME,
          };
          worksheet.getCell(cell_F).numFmt = EXCEL_JS.TIME_FORMAT;
          worksheet.getCell(cell_F).protection = {
            locked: false,
          };
        }


        if (column.key === BULK_UPLOAD.ERROR_KEY) {
          const cell_G = EXCEL_JS.CELL_G + (rowCount);
          worksheet.getCell(cell_G).alignment = { wrapText: true };
          worksheet.getCell(cell_G).protection = {
            locked: false,
          };
        }
      }
    });
    const currentDate = ContentService.GetCache(COMMON_CONSTANTS.PORT_IN_PST_DATE);
    fileName = fileName + COMMON_CONSTANTS.HYPHEN + currentDate.currentPstDateTime;

    worksheet.views = [{ state: EXCEL_JS.FROZEN_STATE, xSplit: VALUE.ZERO, ySplit: VALUE.ONE, topLeftCell: EXCEL_JS.CELL_A2, activeCell: EXCEL_JS.CELL_A1 }];
    this.protectSheet(worksheet);
    // Saving Created Excel Sheet
    workbook.xlsx.writeBuffer().then((data: Uint8Array) => {
      const blob = new Blob([data], { type: EXCEL_JS.APPLICATION_FORMAT_TYPE });
      FileSaver.saveAs(blob, fileName + EXCEL_EXTENSION);
    });
  }

  async dataValidationForChangeNumber(columnName: any[], data_values: any[], fileName: string) {
    const workbook = new Excel.Workbook();
    workbook.views = [{
      x: VALUE.FIVE, y: VALUE.FIVE, width: VALUE.TEN_THOUSAND, height: VALUE.TWENTY_THOUSAND,
      firstSheet: VALUE.ZERO, activeTab: VALUE.ONE, visibility: EXCEL_JS.VISIBLE,
    }];
    const worksheet = workbook.addWorksheet(EXCEL_JS.CHANGE_MSISDN_SHEET);

    worksheet.pageSetup.margins = {
      left: VALUE.POINT_SEVEN, right: VALUE.POINT_SEVEN,
      top: VALUE.POINT_SEVEN_FIVE, bottom: VALUE.POINT_SEVEN_FIVE,
      header: VALUE.POINT_THREE, footer: VALUE.POINT_THREE,
    };

    worksheet.columns = [
      { header: columnName[VALUE.ZERO], key: columnName[VALUE.ZERO], width: VALUE.SIXTEEN },
      { header: columnName[VALUE.ONE], key: columnName[VALUE.ONE], width: VALUE.TWENTY_ONE },
      { header: columnName[VALUE.TWO], key: columnName[VALUE.TWO], width: VALUE.THIRTY },
      { header: columnName[VALUE.THREE], key: columnName[VALUE.THREE], width: VALUE.THIRTY_FIVE },
    ];
    worksheet.addRows(data_values);

    // Data Validation and Formatings for Each Column
    worksheet.eachColumnKey((column: any, index: string) => {
      const row1 = worksheet.getRow(1);
      row1.alignment = { wrapText: true };
      row1.font = { bold: true };

      for (let rowCount = VALUE.TWO; rowCount < VALUE.TWO_THOUSAND_FIVE_HUNDRED_ONE; rowCount++) {

        if (column.key === BULK_UPLOAD.BULK_PHONE_NUMBER_TXT) {
          const cell_A = EXCEL_JS.CELL_A + (rowCount);
          worksheet.getCell(cell_A).dataValidation = {
            type: EXCEL_JS.CUSTOM,
            formulae: ['=AND(ISNUMBER(' + cell_A + '),LEN(' + cell_A + ')=10)'],
            operator: EXCEL_JS.OPERATOR_EQUAL,
            showInputMessage: true,
            allowBlank: true,
            promptTitle: EXCEL_JS.PROMPT_TITLE_PHONE_NUMBER,
            prompt: EXCEL_JS.PROMPT_FORMAT_TEN_DIGITS,
            showErrorMessage: true,
            errorStyle: EXCEL_JS.ERROR_STYLE,
            errorTitle: EXCEL_JS.ERROR_TITLE_PHONE_NUMBER,
            error: EXCEL_JS.ERROR_FORMAT_TEN_DIGITS,
          };
          worksheet.getCell(cell_A).numFmt = EXCEL_JS.NUMBER_FORMAT;
          worksheet.getCell(cell_A).protection = {
            locked: false,
          };
        }

        if (column.key === BULK_UPLOAD.PREFERRED_AREA_CODE) {
          const cell_B = EXCEL_JS.CELL_B + (rowCount);
          worksheet.getCell(cell_B).dataValidation = {
            type: EXCEL_JS.VALIDATION_TYPE_WHOLE,
            formulae: [VALUE.HUNDRED, VALUE.TRIPPLE_NINE],
            operator: EXCEL_JS.OPERATOR_BETWEEN,
            showInputMessage: true,
            allowBlank: true,
            promptTitle: EXCEL_JS.PROMPT_TITLE_AREA_CODE,
            prompt: EXCEL_JS.PROMPT_FORMAT_AREA_CODE,
            showErrorMessage: true,
            errorStyle: EXCEL_JS.ERROR_STYLE,
            errorTitle: EXCEL_JS.ERROR_TITLE_AREA_CODE,
            error: EXCEL_JS.ERROR_AREA_CODE,
          };
          worksheet.getCell(cell_B).protection = {
            locked: false,
          };
        }

        if (column.key === BULK_UPLOAD.CHANGE_NUMBER_REASON_FOR_CHANGE_TXT) {
          const cell_C = EXCEL_JS.CELL_C + (rowCount);
          worksheet.getCell(cell_C).dataValidation = {
            type: EXCEL_JS.VALIDATION_TYPE_LIST,
            formulae: [EXCEL_JS.DROPDOWN_VALUES_REASON_FOR_CHANGE],
            showInputMessage: true,
            allowBlank: true,
            promptTitle: EXCEL_JS.PROMPT_TITLE_REASON_FOR_CHANGE,
            prompt: EXCEL_JS.PROMPT_FORMAT_REASON_FOR_CHANGE,
            showErrorMessage: true,
            errorStyle: EXCEL_JS.ERROR_STYLE,
            errorTitle: EXCEL_JS.ERROR_TITLE_INVALID_VALUE,
            error: EXCEL_JS.PROMPT_FORMAT_REASON_FOR_CHANGE,
          };
          worksheet.getCell(cell_C).protection = {
            locked: false,
          };
        }
      }
    });

    worksheet.views = [{ state: EXCEL_JS.FROZEN_STATE, xSplit: VALUE.ZERO, ySplit: VALUE.ONE, topLeftCell: EXCEL_JS.CELL_A2, activeCell: EXCEL_JS.CELL_A1 }];
    this.protectSheet(worksheet);
    // Saving Created Excel Sheet
    workbook.xlsx.writeBuffer().then((data: Uint8Array) => {
      const blob = new Blob([data], { type: EXCEL_JS.APPLICATION_FORMAT_TYPE });
      FileSaver.saveAs(blob, fileName + EXCEL_EXTENSION);
    });
  }
  async dataValidationForRestoreKey(columnName: any[], data_values: any[], fileName: string) {
    const workbook = new Excel.Workbook();
    workbook.views = [{
      x: VALUE.FIVE, y: VALUE.FIVE, width: VALUE.TEN_THOUSAND, height: VALUE.TWENTY_THOUSAND,
      firstSheet: VALUE.ZERO, activeTab: VALUE.ONE, visibility: EXCEL_JS.VISIBLE,
    }];
    const worksheet = workbook.addWorksheet(EXCEL_JS.RESTORE_SHEET);

    worksheet.pageSetup.margins = {
      left: VALUE.POINT_SEVEN, right: VALUE.POINT_SEVEN,
      top: VALUE.POINT_SEVEN_FIVE, bottom: VALUE.POINT_SEVEN_FIVE,
      header: VALUE.POINT_THREE, footer: VALUE.POINT_THREE,
    };

    worksheet.columns = [
      { header: columnName[VALUE.ZERO], key: columnName[VALUE.ZERO], width: VALUE.SIXTEEN },
      { header: columnName[VALUE.ONE], key: columnName[VALUE.ONE], width: VALUE.NINETEEN },
      { header: columnName[VALUE.TWO], key: columnName[VALUE.TWO], width: VALUE.THIRTY_FIVE },
      { header: columnName[VALUE.THREE], key: columnName[VALUE.THREE], width: VALUE.THIRTY_TWO },
    ];
    if (worksheet.columns[VALUE.TWO].header === EXCEL_JS.RESTORE_KEY_NOTE) {
      const idCol = worksheet.getColumn(EXCEL_JS.RESTORE_KEY_NOTE);
      idCol.width = VALUE.HUNDRED_TWENTY_FIVE;
    }
    worksheet.addRows(data_values);

    // Data Validation and Formatings for Each Column
    worksheet.eachColumnKey((column: any, index: string) => {
      const row1 = worksheet.getRow(1);
      row1.alignment = { wrapText: true };
      row1.font = { bold: true };

      for (let rowCount = VALUE.TWO; rowCount < VALUE.TWO_THOUSAND_FIVE_HUNDRED_ONE; rowCount++) {

        if (column.key === BULK_UPLOAD.BULK_PHONE_NUMBER_TXT) {
          const cell_A = EXCEL_JS.CELL_A + (rowCount);
          worksheet.getCell(cell_A).dataValidation = {
            type: EXCEL_JS.CUSTOM,
            formulae: ['=AND(ISNUMBER(' + cell_A + '),LEN(' + cell_A + ')=10)'],
            operator: EXCEL_JS.OPERATOR_EQUAL,
            showInputMessage: true,
            allowBlank: true,
            promptTitle: EXCEL_JS.PROMPT_TITLE_PHONE_NUMBER,
            prompt: EXCEL_JS.PROMPT_FORMAT_TEN_DIGITS,
            showErrorMessage: true,
            errorStyle: EXCEL_JS.ERROR_STYLE,
            errorTitle: EXCEL_JS.ERROR_TITLE_PHONE_NUMBER,
            error: EXCEL_JS.ERROR_FORMAT_TEN_DIGITS_RESTORE_KEY,
          };
          worksheet.getCell(cell_A).numFmt = EXCEL_JS.NUMBER_FORMAT;
          worksheet.getCell(cell_A).protection = {
            locked: false,
          };
        }

        if (column.key === BULK_UPLOAD.BULK_UPLOAD_RESTORE_DATE) {
          const cell_B = EXCEL_JS.CELL_B + (rowCount);
          worksheet.getCell(cell_B).dataValidation = {
            type: EXCEL_JS.VALIDATION_TYPE_DATE,
            operator: EXCEL_JS.OPERATOR_BETWEEN,
            // formulae: [dayjs().format('L'), dayjs().add(VALUE.TEN, 'day').format('L')],
            showInputMessage: true,
            allowBlank: true,
            promptTitle: EXCEL_JS.PROMPT_TITLE__RESTORE_KEY,
            prompt: EXCEL_JS.PROMPT_FORMAT_DATE,
            showErrorMessage: true,
            errorStyle: EXCEL_JS.ERROR_STYLE,
            errorTitle: EXCEL_JS.ERROR_TITLE_RESTORE_KEY,
            error: EXCEL_JS.ERROR_RESTORE_KEY,
          };
          worksheet.getCell(cell_B).numFmt = EXCEL_JS.DATE_FORMAT;
          worksheet.getCell(cell_B).protection = {
            locked: false,
          };
        }
        if (column.key === EXCEL_JS.RESTORE_KEY_NOTE) {
          const cell_G = EXCEL_JS.CELL_G + (rowCount);
          worksheet.getCell(cell_G).alignment = { wrapText: true };
          worksheet.getCell(cell_G).protection = {
            locked: false,
          };
        }
      }
    });

    worksheet.views = [{ state: EXCEL_JS.FROZEN_STATE, xSplit: VALUE.ZERO, ySplit: VALUE.ONE, topLeftCell: EXCEL_JS.CELL_A2, activeCell: EXCEL_JS.CELL_A1 }];
    this.protectSheet(worksheet);
    // Saving Created Excel Sheet
    workbook.xlsx.writeBuffer().then((data: Uint8Array) => {
      const blob = new Blob([data], { type: EXCEL_JS.APPLICATION_FORMAT_TYPE });
      FileSaver.saveAs(blob, fileName + EXCEL_EXTENSION);
    });
  }

  getCurrentDatePST() {
    const currentDate = ContentService.GetCache(COMMON_CONSTANTS.PORT_IN_PST_DATE);
    if (!isNullOrUndefined(currentDate) && !isNullOrUndefined(currentDate.currentPstDate)) {
      const finalDate = currentDate.currentPstDate.split(COMMON_CONSTANTS.SYMBOLS.FORWARD_SLASH);
      if (!isNullOrUndefined(finalDate) && !isNullOrUndefined(finalDate.length) && finalDate.length !== COMMON_CONSTANTS.DIGITS.ZERO
        && !isNullOrUndefined(finalDate[2]) && !isNullOrUndefined(finalDate[1]) && !isNullOrUndefined(finalDate[0])) {
        return finalDate[2] + COMMON_CONSTANTS.SYMBOLS.COMMA + finalDate[0] + COMMON_CONSTANTS.SYMBOLS.COMMA + finalDate[1];
      }
    }
  }

  // Method to get Max Port In PST Date for excel validations
  getMaxPortInDatePST() {
    const maxDate = ContentService.GetCache(COMMON_CONSTANTS.PORT_IN_PST_DATE);
    if (!isNullOrUndefined(maxDate) && !isNullOrUndefined(maxDate.portInMaxDate)) {
      const finalDate = maxDate.portInMaxDate.split(COMMON_CONSTANTS.SYMBOLS.FORWARD_SLASH);
      if (!isNullOrUndefined(finalDate) && !isNullOrUndefined(finalDate.length) && finalDate.length !== COMMON_CONSTANTS.DIGITS.ZERO
        && !isNullOrUndefined(finalDate[2]) && !isNullOrUndefined(finalDate[1]) && !isNullOrUndefined(finalDate[0])) {
        return finalDate[2] + COMMON_CONSTANTS.SYMBOLS.COMMA + finalDate[0] + COMMON_CONSTANTS.SYMBOLS.COMMA + finalDate[1];
      }
    }
  }

  protectSheet(worksheet) {
    worksheet.protect(BULK_TEMPLATE_FILES.PASSWORD, {
      objects: false,
      scenarios: false,
      selectLockedCells: false,
      selectUnlockedCells: true,
      formatCells: false,
      formatColumns: false,
      formatRows: false,
      insertColumns: false,
      insertRows: false,
      insertHyperlinks: false,
      deleteColumns: false,
      deleteRows: false,
      sort: false,
      autoFilter: true,
      pivotTables: false,
    });
  }

  implementNamedRanges(workbook) {
    const worksheetHidden = workbook.addWorksheet(EXCEL_JS.HIDDEN_SHEET);

    workbook.definedNames.add(EXCEL_JS.HIDDEN_SHEET + '!$A$2:$A$4', SUSPEND_CONSTANTS.TEMP_SUSPEND_UNDERSCORE_TXT);
    workbook.definedNames.add(EXCEL_JS.HIDDEN_SHEET + '!$B$2:$B$6', SUSPEND_CONSTANTS.SUSPEND_WITHOUT_CHARGES_UNDERSCORE_TXT);
    workbook.definedNames.add(EXCEL_JS.HIDDEN_SHEET + '!$C$2:$C$6', SUSPEND_CONSTANTS.SUSPEND_WITH_CHARGES_UNDERSCORE_TXT);
    workbook.definedNames.add(EXCEL_JS.HIDDEN_SHEET + '!$D$2:$D$3', SUSPEND_CONSTANTS.EDUCATION_SUMMER_SUSPEND_UNDERSCORE_TXT);
    workbook.definedNames.add(EXCEL_JS.HIDDEN_SHEET + '!$E$2:$E$4', SUSPEND_CONSTANTS.IMPLEMENTATION_SUSPEND_UNDERSCORE_TXT);
    workbook.definedNames.add(EXCEL_JS.HIDDEN_SHEET + '!$F$2:$F$3', SUSPEND_CONSTANTS.MCSA_CONTRACTUAL_SUSPEND_UNDERSCORE_TXT);
    workbook.definedNames.add(EXCEL_JS.HIDDEN_SHEET + '!$G$2:$G$2', SUSPEND_CONSTANTS.SPECIAL_UNLIMITED_SUSPEND_UNDERSCORE_TXT);
   const hiddenRows = [
       [
        SUSPEND_CONSTANTS.SENIOR_MANAGER,
        SUSPEND_CONSTANTS.SALES_REP_INITIATED,
        SUSPEND_CONSTANTS.SALES_REP_INITIATED,
        SUSPEND_CONSTANTS.DIRECTOR,
        SUSPEND_CONSTANTS.SENIOR_MANAGER,
        SUSPEND_CONSTANTS.VP,
        SUSPEND_CONSTANTS.VP,
      ],
      [
        SUSPEND_CONSTANTS.DIRECTOR_AND_VP,
        SUSPEND_CONSTANTS.B2B_MARKETING_MANAGER,
        SUSPEND_CONSTANTS.B2B_MARKETING_MANAGER,
        SUSPEND_CONSTANTS.GOVERNMENT_SALES,
        SUSPEND_CONSTANTS.DIRECTOR,
        SUSPEND_CONSTANTS.PRICING_TEAM,
        '',
      ],
      [
        SUSPEND_CONSTANTS.SALES_VP_AND_QA_TEAM,
        SUSPEND_CONSTANTS.SALES_DIRECTOR,
        SUSPEND_CONSTANTS.SALES_DIRECTOR,
        '',
        SUSPEND_CONSTANTS.VP,
        '',
        '',
      ],
      [
        '',
        SUSPEND_CONSTANTS.CUSTOMER_INITIATED,
        SUSPEND_CONSTANTS.CUSTOMER_INITIATED,
        '',
        '',
        '',
        '',
      ],
      [
        '',
        SUSPEND_CONSTANTS.VP,
        SUSPEND_CONSTANTS.VP,
        '',
        '',
        '',
        '',
      ],
    ];

    worksheetHidden.columns = [
      { header: SUSPEND_CONSTANTS.TEMP_SUSPEND_UNDERSCORE_TXT, key: SUSPEND_CONSTANTS.TEMP_SUSPEND_UNDERSCORE_TXT, width: VALUE.THIRTY_ONE },
      { header: SUSPEND_CONSTANTS.SUSPEND_WITHOUT_CHARGES_UNDERSCORE_TXT, key: SUSPEND_CONSTANTS.SUSPEND_WITHOUT_CHARGES_UNDERSCORE_TXT, width: VALUE.THIRTY_FIVE },
      { header: SUSPEND_CONSTANTS.SUSPEND_WITH_CHARGES_UNDERSCORE_TXT, key: SUSPEND_CONSTANTS.SUSPEND_WITH_CHARGES_UNDERSCORE_TXT, width: VALUE.THIRTY_ONE },
      { header: SUSPEND_CONSTANTS.EDUCATION_SUMMER_SUSPEND_UNDERSCORE_TXT, key: SUSPEND_CONSTANTS.EDUCATION_SUMMER_SUSPEND_UNDERSCORE_TXT, width: VALUE.THIRTY_ONE },
      { header: SUSPEND_CONSTANTS.IMPLEMENTATION_SUSPEND_UNDERSCORE_TXT, key: SUSPEND_CONSTANTS.IMPLEMENTATION_SUSPEND_UNDERSCORE_TXT, width: VALUE.THIRTY_ONE },
      { header: SUSPEND_CONSTANTS.MCSA_CONTRACTUAL_SUSPEND_UNDERSCORE_TXT, key: SUSPEND_CONSTANTS.MCSA_CONTRACTUAL_SUSPEND_UNDERSCORE_TXT, width: VALUE.THIRTY_ONE },
      { header: SUSPEND_CONSTANTS.SPECIAL_UNLIMITED_SUSPEND_UNDERSCORE_TXT, key: SUSPEND_CONSTANTS.SPECIAL_UNLIMITED_SUSPEND_UNDERSCORE_TXT, width: VALUE.THIRTY_ONE },
      { header: SUSPEND_CONSTANTS.RETENTION_SUSPEND_UNDERSCORE_TXT, key: SUSPEND_CONSTANTS.RETENTION_SUSPEND_UNDERSCORE_TXT, width: VALUE.THIRTY_ONE }
    ];
    worksheetHidden.state = 'veryHidden';
    worksheetHidden.addRows(hiddenRows);
  }

  getLogicalDateForExcel() {
    const todaysDate = new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate());
    const logicalDate = new Date(new Date().setDate(todaysDate.getDate() + VALUE.THIRTY));
    const stringifyLogicalDate = JSON.stringify(logicalDate);
    const logicalSlicedDate = stringifyLogicalDate.slice(VALUE.ONE, VALUE.ELEVEN);
    const splittedLogicalDate = logicalSlicedDate.split(COMMON_CONSTANTS.SYMBOLS.DASH);
    return splittedLogicalDate[VALUE.ZERO] + COMMON_CONSTANTS.SYMBOLS.COMMA + splittedLogicalDate[VALUE.ONE] + COMMON_CONSTANTS.SYMBOLS.COMMA + splittedLogicalDate[VALUE.TWO];
  }

  getCurrentDateForExcel() {
    return new Date().getFullYear() + COMMON_CONSTANTS.SYMBOLS.COMMA + (new Date().getMonth() + COMMON_CONSTANTS.DIGITS.ONE) +
      COMMON_CONSTANTS.SYMBOLS.COMMA + new Date().getDate();
  }

  downloadTransactionHistoryExcel(columnName: any, data_values: any[], fileName: string) {
    const workbook = new Excel.Workbook();
    workbook.views = [{
      x: VALUE.FIVE, y: VALUE.FIVE, width: VALUE.TEN_THOUSAND, height: VALUE.TWENTY_THOUSAND,
      firstSheet: VALUE.ZERO, activeTab: VALUE.ONE, visibility: EXCEL_JS.VISIBLE,
    }];
    const worksheet = workbook.addWorksheet(EXCEL_JS.SHEET_NAME_DATA);
    worksheet.pageSetup.margins = {
      left: VALUE.POINT_SEVEN, right: VALUE.POINT_SEVEN,
      top: VALUE.POINT_SEVEN_FIVE, bottom: VALUE.POINT_SEVEN_FIVE,
      header: VALUE.POINT_THREE, footer: VALUE.POINT_THREE,
    };
    var wbColumns = [];
    for (const name of columnName) {
      let newColumn = {
        header: name,
        key: name,
        width: VALUE.SIXTEEN
      };
      wbColumns.push(newColumn);
    }
    worksheet.columns = wbColumns;

    worksheet.addRows(data_values);
    worksheet.views = [{ state: EXCEL_JS.FROZEN_STATE, xSplit: VALUE.ZERO, ySplit: VALUE.ONE, topLeftCell: EXCEL_JS.CELL_A2, activeCell: EXCEL_JS.CELL_A1 }];
    workbook.xlsx.writeBuffer().then((data: Uint8Array) => {
      const blob = new Blob([data], { type: EXCEL_JS.APPLICATION_FORMAT_TYPE });
      FileSaver.saveAs(blob, fileName + EXCEL_EXTENSION);
    });
  }

  public exportAsExcelFileTransactionHistory(json: any[], excelFileName: string, transDetailsHeader: any[]): void {
    let worksheet: XLSX.WorkSheet;
    const output = transDetailsHeader.concat(json);
    worksheet = XLSX.utils.json_to_sheet(output, { skipHeader: true });
    const workbook: XLSX.WorkBook = { Sheets: { data: worksheet }, SheetNames: ['data'] };
    const excelBuffer: any = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    this.saveAsExcelFile(excelBuffer, excelFileName);
  }

  public exportAsCsvFile(json: any[], csvFileName: string): void {

    const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(json);

    if (!!worksheet.C1 && worksheet.C1.v === 'Suspend_ApproverType') {
      worksheet.C1.v = worksheet.C1.v + + '(not required for Lost/Stolen or Seasonal suspend)';
    }

    const workbook: XLSX.WorkBook = { Sheets: { data: worksheet }, SheetNames: ['data'] };
    const excelBuffer: any = XLSX.write(workbook, { bookType: 'csv', type: 'array' });
    this.saveAsCsvFile(excelBuffer, csvFileName);
  }

  public exportAsXlsxFile(json: any[], xlsxFileName: string): void {
    const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(json);
    const workbook: XLSX.WorkBook = { Sheets: { data: worksheet }, SheetNames: ['data'] };
    const excelBuffer: any = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    this.saveAsExcelFile(excelBuffer, xlsxFileName);
  }

  private saveAsCsvFile(buffer: any, fileName: string): void {
    const data: Blob = new Blob([buffer], {
      type: EXCEL_TYPE,
    });
    FileSaver.saveAs(data, fileName + CSV_EXTENSION);
  }

  incomingfile(event) {
    this.file = event.target.files[0];

  }

  Upload(): any {
    this.bulkFlow = ContentService.GetCache(BULK_UPLOAD.BULK_FLOW_KEY);
    this.templateType = ContentService.GetCache(BULK_UPLOAD.TEMPLATE_KEY);
    if (this.templateType === BULK_UPLOAD.CSV_TXT) {
      this.manageLineService.UploadLatest(this.file);
    } else {
      const fileReader = new FileReader();
      fileReader.onload = (e) => {
        this.arrayBuffer = fileReader.result;
        const data = new Uint8Array(this.arrayBuffer);
        const arr = new Array();
        for (let i = 0; i <= data.length; ++i) {
          arr[i] = String.fromCharCode(data[i]);
        }
        const bstr = arr.join('');
        const workbook = XLSX.read(bstr, { type: 'binary' });
        const first_sheet_name = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[first_sheet_name];

        const sheetValue = workbook.SheetNames[0];
        this.ExceltoJson = XLSX.utils.sheet_to_json(worksheet, { raw: false, defval: '' });
        const tempExcelToJson = [];
        this.ExceltoJson.forEach((item) => {
          const tempItem = { ...item };

         this.iterateOnTempItem(tempItem);
          tempExcelToJson.push(tempItem);
        });
        this.ExceltoJson = [...tempExcelToJson];

        // Start - Logic to remove blank objects like ("" or "   ") from array if whole row of excel is blank.
        // tslint:disable-next-line: prefer-const
        let filtered = this.ExceltoJson.filter(function (a) {
          // tslint:disable-next-line: one-variable-per-declaration
          let temp = Object.keys(a).map((node) => { return a[node]; }), k = temp.join('|');

          if (!this[k] && temp.join('').trim()) {
            this[k] = true;
            return true;
          }
        }, Object.create(null));
        // End - Logic to remove blank objects like ("" or "   ") from array if whole row of excel is blank.
        filtered.validFormate = Boolean(sheetValue);

        this.uploadJson.next(filtered);
      };
      fileReader.readAsArrayBuffer(this.file);
    }
  }

  iterateOnTempItem(tempItem){
    for (const k in tempItem) {
      // tslint:disable-next-line: no-bitwise
      if (~k.indexOf('__EMPTY')) {
        delete tempItem[k];
      }
    }
  }

  downloadChangeServiceDynamicTemplate(selectedServices, templateType) {
    if (templateType === BULK_UPLOAD.CSV_TXT) {
      this.downloadChangeServiceCsv(selectedServices);
    } else if (templateType === BULK_UPLOAD.XLSX_TXT) {
      this.downloadChangeServiceXlsx(selectedServices);
    }
  }

  public downloadCsvOrExcelSheet(excelData: any, worksheetName ,excelType: any) {
    this.manageLineService.close();
    if (_.isEqual(excelType, BULK_UPLOAD.CSV_TXT)) {
      this.exportAsCsvFile(excelData, worksheetName);
    } else if (_.isEqual(excelType, BULK_UPLOAD.XLSX_TXT)) {
      this.exportAsXlsxFile(excelData, worksheetName);
    }
  }

  downloadChangeServiceCsv(selectedServices) {
    const columnHeaders = {};
    columnHeaders[BULK_UPLOAD.BULK_PHONE_NUMBER_TXT] = '';

    for (const service of selectedServices) {
      columnHeaders[service.skuName] = '';
    }
    this.exportAsCsvFile([columnHeaders], BULK_UPLOAD.BULK_TEMPLATE_CHANGE_SERVICE + CSV_EXTENSION);
  }

  downloadChangeServiceXlsx(selectedServices) {
    const workbook = new Excel.Workbook();
    const selectedServiceNames = selectedServices.map(obj => obj.skuName);
    workbook.views = [{
      x: VALUE.FIVE, y: VALUE.FIVE, width: VALUE.TEN_THOUSAND, height: VALUE.TWENTY_THOUSAND,
      firstSheet: VALUE.ZERO, activeTab: VALUE.ONE, visibility: EXCEL_JS.VISIBLE,
    }];
    const worksheet = workbook.addWorksheet(EXCEL_JS.CHANGE_SERVICE_SHEET);
    worksheet.pageSetup.margins = {
      left: VALUE.POINT_SEVEN, right: VALUE.POINT_SEVEN,
      top: VALUE.POINT_SEVEN_FIVE, bottom: VALUE.POINT_SEVEN_FIVE,
      header: VALUE.POINT_THREE, footer: VALUE.POINT_THREE,
    };

    const temp = [{
      header: BULK_UPLOAD.BULK_PHONE_NUMBER_TXT,
      key: BULK_UPLOAD.BULK_PHONE_NUMBER_TXT,
      width: 40
    }]
    for(const name of selectedServiceNames) {
      temp.push({
        header: name,
        key: name,
        width: 40
      });
    }
    worksheet.columns = temp
    worksheet.addRows([]);

    const excelColumns = 'abcdefghijklmnopqrstuvwxyz'.toUpperCase().split('');
    worksheet.eachColumnKey((column: any, index: string) => {
      const row1 = worksheet.getRow(1);
      row1.alignment = { wrapText: true };
      row1.font = { bold: true };

      for (let rowCount = VALUE.TWO; rowCount < VALUE.TWO_THOUSAND_FIVE_HUNDRED_ONE; rowCount++) {
        if (column.key === BULK_UPLOAD.BULK_PHONE_NUMBER_TXT) {
          const cell_A = EXCEL_JS.CELL_A + (rowCount);
          worksheet.getCell(cell_A).dataValidation = {
            type: EXCEL_JS.CUSTOM,
            formulae: ['=AND(ISNUMBER(' + cell_A + '),LEN(' + cell_A + ')=10)'],
            operator: EXCEL_JS.OPERATOR_EQUAL,
            showInputMessage: true,
            allowBlank: true,
            promptTitle: EXCEL_JS.PROMPT_TITLE_PHONE_NUMBER,
            prompt: EXCEL_JS.PROMPT_FORMAT_TEN_DIGITS,
            showErrorMessage: true,
            errorStyle: EXCEL_JS.ERROR_STYLE,
            errorTitle: EXCEL_JS.ERROR_TITLE_PHONENUMBER,
            error: EXCEL_JS.ERROR_FORMAT_TEN_DIGITS,
          };
          worksheet.getCell(cell_A).numFmt = EXCEL_JS.NUMBER_FORMAT;
          worksheet.getCell(cell_A).protection = {
            locked: false,
          };
        } else {

          const cell = excelColumns[column.number - 1] + (rowCount);
          worksheet.getCell(cell).dataValidation = {
            type: EXCEL_JS.VALIDATION_TYPE_LIST,
            formulae: [EXCEL_JS.DROPDOWN_VALUES_CHANGE_SERVICE],
            showInputMessage: true,
            allowBlank: true,
            promptTitle: 'Add or Remove Service',
            prompt: EXCEL_JS.PROMPT_FORMAT_REASON_FOR_CHANGE,
            showErrorMessage: true,
            errorStyle: EXCEL_JS.ERROR_STYLE,
            errorTitle: EXCEL_JS.ERROR_TITLE_INVALID_VALUE,
            error: EXCEL_JS.PROMPT_FORMAT_REASON_FOR_CHANGE,
          }
        }
      }
    });
    worksheet.views = [{ state: EXCEL_JS.FROZEN_STATE, xSplit: VALUE.ZERO, ySplit: VALUE.ONE, topLeftCell: EXCEL_JS.CELL_A2, activeCell: EXCEL_JS.CELL_A1 }];
    workbook.xlsx.writeBuffer().then((data: Uint8Array) => {
      const blob = new Blob([data], { type: EXCEL_JS.APPLICATION_FORMAT_TYPE });
      FileSaver.saveAs(blob, BULK_UPLOAD.BULK_TEMPLATE_CHANGE_SERVICE + EXCEL_EXTENSION);
    }).catch((err) => {
      console.log(err)
    })
  }

  async dataValidationForManagePairing(columnNames: any[], data_values: any[], fileName: string) {
    const workbook = new Excel.Workbook();
    workbook.views = [{
      x: VALUE.FIVE, y: VALUE.FIVE, width: VALUE.TEN_THOUSAND, height: VALUE.TWENTY_THOUSAND,
      firstSheet: VALUE.ZERO, activeTab: VALUE.ONE, visibility: EXCEL_JS.VISIBLE,
    }];
    const worksheet = workbook.addWorksheet(EXCEL_JS.MANAGE_PAIRING_SHEET);

    worksheet.pageSetup.margins = {
      left: VALUE.POINT_SEVEN, right: VALUE.POINT_SEVEN,
      top: VALUE.POINT_SEVEN_FIVE, bottom: VALUE.POINT_SEVEN_FIVE,
      header: VALUE.POINT_THREE, footer: VALUE.POINT_THREE,
    };

    const tempArrayofColumns = [];
    for (const column of columnNames) {
      tempArrayofColumns.push({
        header: column,
        key: column,
        width: 40
      });
    }
    worksheet.columns = tempArrayofColumns;
    worksheet.addRows(data_values);

    worksheet.eachColumnKey((column: any, index: string) => {
      const row1 = worksheet.getRow(1);
      row1.alignment = { wrapText: true };
      row1.font = { bold: true };

      for (let rowCount = VALUE.TWO; rowCount < VALUE.TWO_THOUSAND_FIVE_HUNDRED_ONE; rowCount++) {
        if (column.key === BULK_UPLOAD.MNG_PAIRING_MOBILE_LINE) {
          const cell_A = EXCEL_JS.CELL_A + (rowCount);
          worksheet.getCell(cell_A).dataValidation = {
            type: EXCEL_JS.CUSTOM,
            formulae: ['=AND(ISNUMBER(' + cell_A + '),LEN(' + cell_A + ')=10)'],
            operator: EXCEL_JS.OPERATOR_EQUAL,
            showInputMessage: true,
            allowBlank: true,
            promptTitle: EXCEL_JS.PROMPT_TITLE_MOBILE_LINE,
            prompt: EXCEL_JS.PROMPT_FORMAT_TEN_DIGITS,
            showErrorMessage: true,
            errorStyle: EXCEL_JS.ERROR_STYLE,
            errorTitle: EXCEL_JS.ERROR_TITLE_MOBILE_LINE,
            error: EXCEL_JS.ERROR_FORMAT_TEN_DIGITS,
          };
          worksheet.getCell(cell_A).numFmt = EXCEL_JS.NUMBER_FORMAT;
          worksheet.getCell(cell_A).protection = {
            locked: false,
          };
        } else if (column.key === BULK_UPLOAD.MNG_PAIRING_DIGITS_LINE) {
          const cell_B = EXCEL_JS.CELL_B + (rowCount);
          worksheet.getCell(cell_B).dataValidation = {
            type: EXCEL_JS.CUSTOM,
            formulae: ['=AND(ISNUMBER(' + cell_B + '),LEN(' + cell_B + ')=10)'],
            operator: EXCEL_JS.OPERATOR_EQUAL,
            showInputMessage: true,
            allowBlank: true,
            promptTitle: EXCEL_JS.PROMPT_TITLE_DIGITS_LINE,
            prompt: EXCEL_JS.PROMPT_FORMAT_TEN_DIGITS,
            showErrorMessage: true,
            errorStyle: EXCEL_JS.ERROR_STYLE,
            errorTitle: EXCEL_JS.ERROR_TITLE_DIGITS_LINE,
            error: EXCEL_JS.ERROR_FORMAT_TEN_DIGITS,
          };
          worksheet.getCell(cell_B).numFmt = EXCEL_JS.NUMBER_FORMAT;
          worksheet.getCell(cell_B).protection = {
            locked: false,
          };
        } else if (column.key === BULK_UPLOAD.MNG_PAIRING_ACTION) {
          const cell_C = EXCEL_JS.CELL_C + (rowCount);
          worksheet.getCell(cell_C).dataValidation = {
            type: EXCEL_JS.VALIDATION_TYPE_LIST,
            formulae: [EXCEL_JS.DROPDOWN_VALUES_MANAGE_PAIRING],
            showInputMessage: true,
            allowBlank: true,
            promptTitle: EXCEL_JS.PROMPT_TITLE_ACTION,
            prompt: EXCEL_JS.PROMPT_SELECT_ACTION,
            showErrorMessage: true,
            errorStyle: EXCEL_JS.ERROR_STYLE,
            errorTitle: EXCEL_JS.PROMPT_INVALID_ACTION_TITLE,
            error: EXCEL_JS.PROMPT_INVALID_ACTION,
          };
        }
      }
    });
    worksheet.views = [{ state: EXCEL_JS.FROZEN_STATE, xSplit: VALUE.ZERO, ySplit: VALUE.ONE, topLeftCell: EXCEL_JS.CELL_A2, activeCell: EXCEL_JS.CELL_A1 }];
    workbook.xlsx.writeBuffer().then((data: Uint8Array) => {
      const blob = new Blob([data], { type: EXCEL_JS.APPLICATION_FORMAT_TYPE });
      FileSaver.saveAs(blob, fileName + EXCEL_EXTENSION);
    }).catch((err) => {
      console.log(err)
    });
  }

  async dataValidationForStrategicPortIn(columnNames: any[], data_values: any[], fileName: string) {
    const workbook = new Excel.Workbook();
    workbook.views = [{
      x: VALUE.FIVE, y: VALUE.FIVE, width: VALUE.TEN_THOUSAND, height: VALUE.TWENTY_THOUSAND,
      firstSheet: VALUE.ZERO, activeTab: VALUE.ONE, visibility: EXCEL_JS.VISIBLE,
    }];
    const worksheet = workbook.addWorksheet(EXCEL_JS.RESEND_SHEET);

    worksheet.pageSetup.margins = {
      left: VALUE.POINT_SEVEN, right: VALUE.POINT_SEVEN,
      top: VALUE.POINT_SEVEN_FIVE, bottom: VALUE.POINT_SEVEN_FIVE,
      header: VALUE.POINT_THREE, footer: VALUE.POINT_THREE,
    };

    const tempArrayofColumns = [];
    for (const column of columnNames) {
      tempArrayofColumns.push({
        header: column,
        key: column,
        width: 40
      });
    }
    worksheet.columns = tempArrayofColumns;
    worksheet.addRows(data_values);

    worksheet.eachColumnKey((column: any, index: string) => {
      const row1 = worksheet.getRow(1);
      row1.alignment = { wrapText: true };
      row1.font = { bold: true };
    });
    worksheet.views = [{ state: EXCEL_JS.FROZEN_STATE, xSplit: VALUE.ZERO, ySplit: VALUE.ONE, topLeftCell: EXCEL_JS.CELL_A2, activeCell: EXCEL_JS.CELL_A1 }];
    workbook.xlsx.writeBuffer().then((data: Uint8Array) => {
      const blob = new Blob([data], { type: EXCEL_JS.APPLICATION_FORMAT_TYPE });
      FileSaver.saveAs(blob, fileName + EXCEL_EXTENSION);
    }).catch((err) => {
      console.log(err)
    });
  }

}
